
import { useState, useEffect } from 'react';
import { nuiteeBookingService, type HotelPlaceIdsResponse } from '@/services/nuiteeBookingService';
import { debug, error as logError } from '@/utils/logger';

export interface NuiteeHotelEnrichment {
  /** Resolved Place IDs, or null while loading / if fetch failed. */
  enrichedIds: HotelPlaceIdsResponse | null;
  /** True while the Place IDs endpoint is in-flight. */
  isEnriching: boolean;
}

/**
 * Fetches hotel-specific and city-level Google Place IDs for a Nuitee hotel on demand.
 * Results are cached in the service layer so the same hotel is never re-fetched within a session.
 * Pass null/undefined to skip enrichment (non-Nuitee places).
 */
export function useNuiteeHotelEnrichment(nuiteeHotelId: string | null | undefined): NuiteeHotelEnrichment {
  const [enrichedIds, setEnrichedIds] = useState<HotelPlaceIdsResponse | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);

  useEffect(() => {
    if (!nuiteeHotelId) {
      setEnrichedIds(null);
      setIsEnriching(false);
      return;
    }

    let cancelled = false;
    setIsEnriching(true);

    nuiteeBookingService
      .getHotelPlaceIds(nuiteeHotelId)
      .then((result) => {
        if (cancelled) return;
        debug('[useNuiteeHotelEnrichment] Place IDs resolved', { nuiteeHotelId, result });
        setEnrichedIds(result);
      })
      .catch((err) => {
        if (cancelled) return;
        logError('[useNuiteeHotelEnrichment] Failed to fetch Place IDs', err);
        setEnrichedIds(null);
      })
      .finally(() => {
        if (!cancelled) setIsEnriching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nuiteeHotelId]);

  return { enrichedIds, isEnriching };
}
